import React, { useRef, useState } from 'react';
import "./contact.css";

const Contact = () => {
    const form = useRef();
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        from_name: '',
        from_email: '',
        from_phone: '',
        company: '',
        project_type: '',
        budget: '',
        timeline: '',
        message: '',
        website: '' // Honeypot - hidden from humans, bots fill it
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const sendEmail = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        
        // Prepare data for backend API
        const apiData = {
            name: formData.from_name,
            email: formData.from_email,
            phone: formData.from_phone,
            company: formData.company,
            service: formData.project_type,
            budget: formData.budget,
            timeline: formData.timeline,
            message: formData.message,
            website: formData.website, // Honeypot - server drops submission if filled
            source: 'contact' // Indicate this came from contact form
        };

        try {
            // Send to YOUR backend API instead of EmailJS
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/leads/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(apiData)
            });

            const result = await response.json();

            if (response.ok && result.success) {
                // Show success message with better UX
                const successMessage = document.createElement('div');
                successMessage.className = 'contact__success-modal';
                successMessage.innerHTML = `
                    <div class="success-modal-content">
                        <div class="success-icon">✅</div>
                        <h2>Message Sent Successfully!</h2>
                        <p>Thank you for reaching out! I'll review your project details and get back to you within 2 hours.</p>
                        <p class="success-next-steps">In the meantime, you can:</p>
                        <div class="success-actions">
                            <button onclick="window.location.href='/login'" class="success-btn primary">
                                Sign In to Track Progress
                            </button>
                            <button onclick="this.closest('.contact__success-modal').remove()" class="success-btn secondary">
                                Continue Browsing
                            </button>
                        </div>
                        <p class="success-timer">Redirecting to sign in page in <span id="countdown">10</span> seconds...</p>
                    </div>
                `;
                document.body.appendChild(successMessage);
                
                // Reset form
                setFormData({
                    from_name: '',
                    from_email: '',
                    from_phone: '',
                    company: '',
                    project_type: '',
                    budget: '',
                    timeline: '',
                    message: '',
                    website: ''
                });
                form.current.reset();
                
                // Countdown and redirect
                let seconds = 10;
                const countdownElement = document.getElementById('countdown');
                const countdownInterval = setInterval(() => {
                    seconds--;
                    if (countdownElement) {
                        countdownElement.textContent = seconds;
                    }
                    if (seconds <= 0) {
                        clearInterval(countdownInterval);
                        window.location.href = '/login';
                    }
                }, 1000);
                
                // Allow user to close modal and stop redirect
                successMessage.addEventListener('click', (e) => {
                    if (e.target === successMessage || e.target.classList.contains('secondary')) {
                        clearInterval(countdownInterval);
                        successMessage.remove();
                    }
                });
            } else {
                throw new Error(result.error || 'Failed to send message');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Failed to send message. Please email me directly at spinozadelva@gmail.com');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <section className="contact section" id="contact">
            <h2 className="section__title">Get in touch</h2>
            <span className="section__subtitle">Let's Discuss Your Project</span>

            <div className="contact__container container">
                
                {/* Quick Contact Buttons */}
                <div className="contact__quick-buttons">
                    <a href="mailto:spinozadelva@gmail.com" className="quick-contact-btn email-btn">
                        <i className="bx bx-mail-send"></i>
                        <span>Email</span>
                    </a>
                    <a href="https://wa.me/19178087320?text=Hi%20Spinoza!%20I%20need%20a%20website" 
                       target="_blank" 
                       rel="noopener noreferrer"
                       className="quick-contact-btn whatsapp-btn">
                        <i className="bx bxl-whatsapp"></i>
                        <span>WhatsApp</span>
                    </a>
                    <a href="https://www.linkedin.com/in/spinozadelva" 
                       target="_blank" 
                       rel="noopener noreferrer"
                       className="quick-contact-btn linkedin-btn">
                        <i className="bx bxl-linkedin"></i>
                        <span>LinkedIn</span>
                    </a>
                    <a href="tel:+19178087320" className="quick-contact-btn phone-btn">
                        <i className="bx bx-phone"></i>
                        <span>Call</span>
                    </a>
                </div>

                {/* Main Contact Form */}
                <div className="contact__form-wrapper">
                    <div className="contact__form-header">
                        <h3>Tell me about your project</h3>
                        <p>Fill out the form below and I'll get back to you within 2 hours</p>
                    </div>
                    
                    <form ref={form} onSubmit={sendEmail} className='contact__form'>
                        {/* Honeypot - invisible to humans (off-screen, not display:none
                            so smarter bots don't skip it). Bots autofill it; server
                            silently drops any submission where this has a value. */}
                        <input
                            type="text"
                            name="website"
                            value={formData.website}
                            onChange={handleInputChange}
                            tabIndex="-1"
                            autoComplete="off"
                            aria-hidden="true"
                            style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0, pointerEvents: 'none' }}
                        />
                        <div className="contact__form-grid">
                            {/* Row 1: Name and Email */}
                            <div className="contact__form-group">
                                <label className="contact__form-label">
                                    <i className="bx bx-user"></i>
                                    Full Name <span className="required">*</span>
                                </label>
                                <input 
                                    type="text" 
                                    name='from_name'
                                    value={formData.from_name}
                                    onChange={handleInputChange}
                                    className='contact__form-input' 
                                    placeholder='John Doe'
                                    required
                                />
                            </div>

                            <div className="contact__form-group">
                                <label className="contact__form-label">
                                    <i className="bx bx-envelope"></i>
                                    Email Address <span className="required">*</span>
                                </label>
                                <input 
                                    type="email" 
                                    name='from_email'
                                    value={formData.from_email}
                                    onChange={handleInputChange}
                                    className='contact__form-input' 
                                    placeholder='john@example.com'
                                    required
                                />
                            </div>

                            {/* Row 2: Phone and Company */}
                            <div className="contact__form-group">
                                <label className="contact__form-label">
                                    <i className="bx bx-phone"></i>
                                    Phone Number
                                </label>
                                <input 
                                    type="tel" 
                                    name='from_phone'
                                    value={formData.from_phone}
                                    onChange={handleInputChange}
                                    className='contact__form-input' 
                                    placeholder='(555) 123-4567'
                                />
                            </div>

                            <div className="contact__form-group">
                                <label className="contact__form-label">
                                    <i className="bx bx-building"></i>
                                    Company/Organization
                                </label>
                                <input 
                                    type="text" 
                                    name='company'
                                    value={formData.company}
                                    onChange={handleInputChange}
                                    className='contact__form-input' 
                                    placeholder='Your Company Name'
                                />
                            </div>

                            {/* Row 3: Project Type and Budget */}
                            <div className="contact__form-group">
                                <label className="contact__form-label">
                                    <i className="bx bx-category"></i>
                                    Project Type <span className="required">*</span>
                                </label>
                                <select 
                                    name='project_type'
                                    value={formData.project_type}
                                    onChange={handleInputChange}
                                    className='contact__form-input contact__form-select'
                                    required
                                >
                                    <option value="">Select Project Type</option>
<option value="restaurant">Restaurant Online Ordering</option>
<option value="smallbusiness">Small Business Website</option>
<option value="ecommerce">E-Commerce Store</option>
<option value="medical">Healthcare / Medical Portal</option>
<option value="realestate">Real Estate Platform</option>
<option value="webapp">Custom Web Application</option>
<option value="maintenance">Maintenance & SEO</option>
<option value="consultation">Consultation Only</option>
<option value="other">Other</option>
                                </select>
                            </div>

                            <div className="contact__form-group">
                                <label className="contact__form-label">
                                    <i className="bx bx-wallet"></i>
                                    Budget
                                </label>
                                <input
                                    type="text"
                                    name='budget'
                                    value={formData.budget}
                                    onChange={handleInputChange}
                                    className='contact__form-input'
                                    placeholder="Tell me what you're working with"
                                />
                                <span className="contact__form-hint">
                                    No fixed packages share a number or a range and I'll shape the project to fit.
                                </span>
                            </div>

                            {/* Row 4: Timeline */}
                            <div className="contact__form-group form-group-full">
                                <label className="contact__form-label">
                                    <i className="bx bx-calendar"></i>
                                    Project Timeline
                                </label>
                                <select 
                                    name='timeline'
                                    value={formData.timeline}
                                    onChange={handleInputChange}
                                    className='contact__form-input contact__form-select'
                                >
                                    <option value="">When do you need this completed?</option>
                                    <option value="asap">ASAP</option>
                                    <option value="1-2-weeks">1-2 Weeks</option>
                                    <option value="1-month">Within 1 Month</option>
                                    <option value="2-3-months">2-3 Months</option>
                                    <option value="flexible">I'm Flexible</option>
                                </select>
                            </div>

                            {/* Row 5: Message */}
                            <div className="contact__form-group form-group-full">
                                <label className="contact__form-label">
                                    <i className="bx bx-message-detail"></i>
                                    Project Details <span className="required">*</span>
                                </label>
                                <textarea 
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    rows="6"
                                    className='contact__form-input contact__form-textarea' 
                                    placeholder='Describe your project, specific features you need, or any questions you have...'
                                    required
                                ></textarea>
                            </div>
                        </div>

                        {/* File Upload Note */}
                        <div className="contact__form-note">
                            <i className="bx bx-info-circle"></i>
                            <span>Have project files? You can share them after we connect via email.</span>
                        </div>
                        
                        <button 
                            type="submit" 
                            className="contact__submit-btn"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <span className="loading-spinner"></span>
                                    Sending...
                                </>
                            ) : (
                                <>
                                    Send Message
                                    <i className="bx bx-send"></i>
                                </>
                            )}
                        </button>
                    </form>
                </div>

                {/* Contact Info Section */}
                <div className="contact__info-section">
                    <div className="contact__info-item">
                        <i className="bx bx-map"></i>
                        <div>
                            <h4>Location</h4>
                            <p>New York, USA</p>
                        </div>
                    </div>
                    <div className="contact__info-item">
                        <i className="bx bx-time"></i>
                        <div>
                            <h4>Response Time</h4>
                            <p>Within 2 hours</p>
                        </div>
                    </div>
                    <div className="contact__info-item">
                        <i className="bx bx-envelope"></i>
                        <div>
                            <h4>Email</h4>
                            <p>spinozadelva@gmail.com</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;